import { getCookie, getStorage, removeStorage, setStorage } from "../store/localStorage";
import { USER_MANUAL_PATH } from "../constants/routes";

// Sends the user back to a page outside this SPA after login - today only the
// user manual, which redirects to `/login?returnUrl=<its page>` when there is no
// session. Only same-origin paths under an allowed prefix are honoured, so the
// parameter cannot be used as an open redirect.
const ALLOWED_PREFIXES = [USER_MANUAL_PATH];
const STORAGE_KEY = "postLoginReturnUrl";
const MAX_AGE_MS = 10 * 60 * 1000;

const sanitizeReturnUrl = (value) => {
    if (!value) return null;
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        const path = url.pathname.endsWith("/") ? url.pathname : `${url.pathname}/`;
        if (!ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix))) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
};

const getReturnUrlFromQuery = (search) =>
    sanitizeReturnUrl(new URLSearchParams(search).get("returnUrl"));

// Called when the login page opens. Google, Okta and SAML leave the SPA and come
// back on a callback route, so the query string is kept in sessionStorage until
// login finishes. Opening the login page without one clears any stale value.
export const rememberReturnUrl = (search) => {
    const url = getReturnUrlFromQuery(search);
    if (url) setStorage(STORAGE_KEY, { url, savedAt: Date.now() });
    else removeStorage(STORAGE_KEY);
};

const getPendingReturnUrl = () => {
    const saved = getStorage(STORAGE_KEY);
    if (!saved || Date.now() - saved.savedAt > MAX_AGE_MS) return null;
    return sanitizeReturnUrl(saved.url);
};

// The manual sends users here when their session is missing or expired, and these
// guards run before Layout's expiry watcher logs them out. Without this check an
// expired cookie that the browser has not dropped yet would bounce between the two.
const isSessionExpired = () => {
    const expiry = getCookie("jwtExpirationTime");
    return !!expiry && new Date(expiry).getTime() <= Date.now();
};

// For the route guards once the user is logged in: the page to leave the SPA for, or null.
export const getPostLoginReturnUrl = (search = "") => {
    if (isSessionExpired()) return null;
    return getReturnUrlFromQuery(search) || getPendingReturnUrl();
};

export const clearPendingReturnUrl = () => removeStorage(STORAGE_KEY);
